import { useState } from 'react'

// Tickers whose Parqet logo 404'd this session (→ letter fallback). Surfaces
// that want a logo-only cluster (e.g. the Overview markets door) filter against
// this — it fills in as logos load/fail across the app. Tickers NOT in here
// either have a logo or haven't been rendered yet.
export const LOGO_MISSING = new Set()

// App-wide logo shape: CIRCLE (chosen Jun 13; see v3/atlas-core.jsx +
// CURRENT-STATE "NO white plate, no drop shadow… real parqet logo image").
// Flip the global to false to revert to rounded-square; any call can override
// per-instance via `circle`. `plate` opts into the old paper "coin" look
// (warm-paper disc + ~9% inset) — OFF by default (Ben removed the coin).
if (typeof window !== 'undefined' && window.__atlasLogoCircle === undefined) window.__atlasLogoCircle = true

// <StockLogo ticker="NVDA" size={24} />
// Fetches via /api/stock-logo (302 → Parqet CDN, #407). On load error falls
// back to BARE ticker initials — NO grey box (BEN-OVERRIDE global hard rule:
// never a grey icon-tile placeholder anywhere; a miss reads as plain initials,
// not a box). Stays on the amber/neutral v3 system.
export function StockLogo({ ticker, size = 24, rounded = 6, circle, plate = false }) {
  const [error, setError] = useState(false)
  const [loaded, setLoaded] = useState(false)
  const t = (ticker || '').toUpperCase()

  // Reused elements get a NEW ticker prop (dock header, re-sorted rows): reset
  // the load/error state or the old symbol's logo/initials linger until the new
  // src decodes. Known-404 tickers go straight to initials — no refetch flash.
  const [prevT, setPrevT] = useState(t)
  if (prevT !== t) { setPrevT(t); setLoaded(false); setError(LOGO_MISSING.has(t)) }

  if (!t) return null

  // Default to the app-wide circle; `circle` prop overrides per-instance.
  const circ = circle != null ? circle : (typeof window !== 'undefined' && !!window.__atlasLogoCircle)
  const radius = circ ? '50%' : rounded

  if (error) {
    return (
      <div
        title={t}
        style={{
          width: size,
          height: size,
          flexShrink: 0,
          background: 'transparent',
          color: 'hsl(var(--studio-text-subtle))',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: 'var(--studio-font-mono), JetBrains Mono, monospace',
          fontWeight: 600,
          fontSize: Math.max(8, size * 0.36),
          letterSpacing: -0.3,
          lineHeight: 1,
        }}
      >
        {t.slice(0, t.length <= 4 ? t.length : 3)}
      </div>
    )
  }

  return (
    <img
      src={`/api/stock-logo?ticker=${t}`}
      alt={t}
      width={size}
      height={size}
      onError={() => { LOGO_MISSING.add(t); setError(true) }}
      // Fade in on load so lists don't paint raggedly; cached images resolve
      // instantly via img.complete (the ref check), so no flash on re-renders.
      onLoad={() => setLoaded(true)}
      ref={(el) => { if (el?.complete && el.naturalWidth > 0) setLoaded(true) }}
      style={{
        opacity: loaded ? 1 : 0,
        transition: 'opacity 150ms ease',
        width: size,
        height: size,
        flexShrink: 0,
        borderRadius: radius,
        // object-fit: contain so the logo scales to fit the circle without
        // cropping/distorting; padding 0 (no inset ring) so the artwork runs
        // edge-to-edge to the rim — the circle IS the logo, not a frame around
        // it. plate mode brings back the warm-paper coin (inset + disc bg).
        objectFit: 'contain',
        background: plate ? 'var(--studio-paper, #E9E3D6)' : 'hsl(var(--studio-surface-2))',
        padding: plate ? Math.round(size * 0.09) : 0,
        boxSizing: 'border-box',
        display: 'block',
      }}
    />
  )
}
